import * as React from 'react';
import type { PressEvent } from 'react-aria';

import type { GestureResponderEvent } from '@gitbook/react-uikit';

import { NavigationFunctionsContext } from './contexts';
import type { NavigateFunction, NavigationTarget } from './types';
import { usePassiveIsCurrentScreen } from './useIsCurrentScreen';

export interface NavigationLinkProps {
    href: string;
    onPress: (event?: GestureResponderEvent) => void;
}

/**
 * Returns
 * - a function to navigate to another url
 * - a function to get the pathname of a navigation to another url
 * - a function to generate the props necesserary for a proper link to a given target
 */
export function useNavigation() {
    const { forceHardNavigation, navigate, getNavigationURL, getNavigationState } =
        React.useContext(NavigationFunctionsContext);
    const isCurrentScreenRef = usePassiveIsCurrentScreen();

    const navigateInScreen = React.useCallback<NavigateFunction>(
        (target, options) => {
            // when navigation is not coming from current screen and we want to update the current one
            // we adapt the current to match its relative one (the backgound)
            if ('current' in target && !isCurrentScreenRef.current) {
                target = {
                    background: target.current,
                };
            }

            navigate(target, options);
        },
        [navigate, isCurrentScreenRef]
    );

    const getNavigationLinkProps = React.useCallback(
        (
            to: NavigationTarget,
            onPress?: (event?: GestureResponderEvent | PressEvent) => void
        ): NavigationLinkProps => ({
            href: getNavigationURL(to),
            onPress: (event?: GestureResponderEvent | PressEvent) => {
                if (onPress) {
                    onPress(event);
                }

                if (event) {
                    if ('isDefaultPrevented' in event && event.isDefaultPrevented()) {
                        return;
                    }

                    if (forceHardNavigation) {
                        // Let the browser handle the navigation
                        return;
                    }

                    const eventWithKey = 'nativeEvent' in event ? event.nativeEvent : event;

                    // if they want to open in a new tab, let them
                    if (
                        ('metaKey' in eventWithKey && eventWithKey.metaKey) ||
                        ('ctrlKey' in eventWithKey && eventWithKey.ctrlKey)
                    ) {
                        // browser will handle it
                        return;
                    }

                    if ('preventDefault' in event) {
                        event.preventDefault();
                    }
                }

                navigate(to);
            },
        }),
        [forceHardNavigation, getNavigationURL, navigate]
    );

    /**
     * Navigate to a hash anchor. Also handles any scrolling if necessary.
     */
    const navigateToHash = React.useCallback(
        (
            anchor: string,
            options: {
                /**
                 * If false, prevent scrolling the window
                 */
                scroll: boolean;
            } = { scroll: true }
        ) => {
            if (options.scroll) {
                if (typeof window !== 'undefined') {
                    const sectionEl = window.document.getElementById(anchor);
                    if (sectionEl) {
                        sectionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });

                        // Through the router, so the screen knows about the hash and can tell
                        // when a later navigation removes it.
                        navigateInScreen({ current: { hash: anchor } });
                    }
                }
            } else {
                // If we're not scrolling, use `replaceState` to avoid pushing onto the history.
                if (!anchor) {
                    // Remove the hash from the URL
                    window.history.replaceState(
                        null,
                        '',
                        window.location.pathname + window.location.search
                    );
                } else {
                    window.history.replaceState(null, '', `#${anchor}`);
                }
            }

            // We have to trigger the `hashchange` event manually because
            // it's overriden by the `pushState` and `replaceState` calls.
            window.dispatchEvent(new HashChangeEvent('hashchange'));
        },
        [navigateInScreen]
    );

    return {
        navigate: navigateInScreen,
        navigateToHash,
        getNavigationURL,
        getNavigationLinkProps,
        getNavigationState,
    };
}
